import { CalendarClock } from 'lucide-react';
import { useState } from 'react';

import { ConditionalWrapper } from '@/components/conditional-wrapper';
import { iconStyles, triggerButtonStyles } from '@/components/date-picker';
import { Icon } from '@/components/icon';
import { Label } from '@/components/inputs';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/popover';
import { cn } from '@/utils/styles';

import { TimeRange } from '../ui/time-range';

export interface TimePickerProps {
  value?: string;
  onChange: (value: string | undefined) => void;
  label?: string | null;
  buttonLabel?: string;
  isPopoverOpen?: boolean;
  disabled?: boolean;
  isLocked?: boolean;
}

export const TimePicker = ({
  value,
  onChange,
  label = 'Times',
  buttonLabel,
  isPopoverOpen,
  disabled,
  isLocked,
}: TimePickerProps) => {
  const [isOpen, setIsOpen] = useState<boolean | undefined>(isPopoverOpen);

  return (
    <div>
      <Label htmlFor="">{label}</Label>
      {/* modal={true} setting required for this to work inside the DrawerAlt component to avoid any focus trap collisions */}
      <Popover open={isOpen} onOpenChange={setIsOpen} modal>
        <ConditionalWrapper
          condition={!isLocked}
          // eslint-disable-next-line react/no-unstable-nested-components
          wrapper={(conditionChildren) => {
            return <PopoverTrigger asChild>{conditionChildren}</PopoverTrigger>;
          }}
        >
          <button type="button" className={cn(triggerButtonStyles({ isOpen, isDisabled: disabled }), 'flex gap-2')}>
            {value || buttonLabel}
            {isLocked ? (
              <Icon
                name={isLocked ? 'lock' : 'calendar-clock'}
                className={iconStyles({ isLocked: !!isLocked })}
                size="base"
              />
            ) : (
              <CalendarClock size={16} />
            )}
          </button>
        </ConditionalWrapper>
        <PopoverContent className="bg-white p-6">
          <div className="flex min-w-80 flex-col gap-6">
            <TimeRange defaultTime={value} onTimeChange={(newTime) => onChange(newTime)} />
            <span className="text-center text-sm text-grey-400">Drag the slider to change the time</span>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};
